// @ts-strict-ignore
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { SohoAlertDirective, SohoMessageService, SohoSpinboxComponent } from 'ids-enterprise-ng';
import { LMLoggerService, LMSpecService } from '@app/services';
import { LMComputeBuilderService } from './services';
import { LMFieldUtilService } from '@services/utils/lm-field-util.service';
import * as urltemplate from 'url-template';
import { Subject } from 'rxjs';
import {debounceTime, filter, switchMap} from 'rxjs/operators';

@Component({
  selector: 'lm-derived-compute-builder',
  templateUrl: './lm-derived-compute-builder.component.html',
  styleUrls: ['./lm-derived-compute-builder.component.scss']
})
export class LMDerivedComputeBuilderComponent implements OnInit, OnDestroy {

  @ViewChild('definition', {read: SohoAlertDirective}) definitionAlert: SohoAlertDirective;
  @ViewChild('size', {read: SohoAlertDirective}) sizeAlert: SohoAlertDirective;
  @ViewChild('decimal', {read: SohoAlertDirective}) decimalAlert: SohoAlertDirective;
  @ViewChild('fieldName', {read: SohoAlertDirective}) fieldNameAlert: SohoAlertDirective;
  @ViewChild('fieldType', {read: SohoAlertDirective}) fieldTypeAlert: SohoAlertDirective;

  @Input() businessClass: string;
  @Input() lookupUrl: string;
  @Input() lplValidationUrl: string;
  @Input() derivedLpl: string;
  @Input() resourceId: string;

  preview: string;

  isSizeReadOnly = false;
  isDecimalReadOnly = false;

  private compiledValidationUrl: string;

  private queryValidateEventDebouncer$ = new Subject<string>();
  private fieldNameValidateEventDebouncer$ = new Subject<string>();

  // Custom Compute properties
  customCompute: LMCustomCompute = {
    fieldName: '',
    fieldType: '',
    size: '',
    decimal: ''
  };

  fieldTypes: Object[] = [
    { value: 'Date', label: Soho.Locale.translate('Date')},
    { value: 'Decimal', label: Soho.Locale.translate('DecimalLabel')},
    { value: 'Unsigned Decimal', label: Soho.Locale.translate('UnsignedDecimalLabel')},
    { value: 'Integer', label: Soho.Locale.translate('IntegerLabel')},
    { value: 'Numeric', label: Soho.Locale.translate('NumericLabel')},
    { value: 'Percent', label: Soho.Locale.translate('PercentLabel')},
    { value: 'Unsigned Percent', label: Soho.Locale.translate('UnsignedPercentLabel')},
    { value: 'Period', label: Soho.Locale.translate('Period')},
    { value: 'Time', label: Soho.Locale.translate('Time')}
  ];

  typeAutomationId = [{ name: 'data-automation-id', value: 'lm-derived-compute-builder-type' }];

  constructor(private lmSpecService: LMSpecService,
    private lmLoggerService: LMLoggerService,
    private changeDetectorRef: ChangeDetectorRef,
    private sohoMessageService: SohoMessageService,
    private lmComputeBuilderService: LMComputeBuilderService,
    private lmFieldUtilService: LMFieldUtilService) { }

  ngOnInit(): void {
    this.isDecimalReadOnly = true;

    if (this.derivedLpl) {
      this.customCompute = this.lmComputeBuilderService.parseDerivedLpl(this.derivedLpl);
      this.handleFieldType();
    }

    const compiledUrlTemplate = urltemplate.parse(this.lplValidationUrl);
    this.compiledValidationUrl = compiledUrlTemplate.expand({ resourceId: this.resourceId});

    this.queryValidateEventDebouncer$
      .pipe(
        debounceTime(250),
        switchMap((lpl) => this.lmComputeBuilderService.computeIsValid(this.compiledValidationUrl, this.businessClass, lpl, 'derived'))
      ).subscribe((result: LMLplValidationResponse) => {
        if (this.definitionAlert) {
          this.definitionAlert.removeAllMessages();
        }

        if (result.lplStatus
          && result.lplStatus !== 'LPL_VALID'
          && result.validationError
          && this.definitionAlert) {
          this.definitionAlert.addInlineMessage(result.validationError.errorDescription);
        }
        this.handleFieldChangedEvent();
      });

    this.fieldNameValidateEventDebouncer$
      .pipe(
        filter(() => !!this.customCompute.fieldName),
        debounceTime(500))
      .subscribe(() => this.validateFieldName(this.customCompute.fieldName));
  }

  ngOnDestroy(): void {
    this.queryValidateEventDebouncer$.complete();
    this.fieldNameValidateEventDebouncer$.complete();
  }

  handleFieldType(value?: string) {

    this.customCompute.fieldType = value ? value : this.customCompute.fieldType;
    this.customCompute.size = this.customCompute.size || '';
    this.customCompute.decimal = this.customCompute.decimal || '';

    if (this.customCompute.fieldType === 'Date') {

      this.customCompute.size = '8';
      this.customCompute.decimal = '';
      this.isSizeReadOnly = true;
      this.isDecimalReadOnly = true;

    } else if (this.customCompute.fieldType === 'Decimal'
      || this.customCompute.fieldType === 'Unsigned Decimal') {

      this.isSizeReadOnly = false;
      this.isDecimalReadOnly = false;
    } else if (this.customCompute.fieldType === 'Integer') {

      this.customCompute.decimal = '';
      this.isSizeReadOnly = false;
      this.isDecimalReadOnly = true;
    } else if (this.customCompute.fieldType === 'Numeric') {

      this.customCompute.decimal = '';
      this.isSizeReadOnly = false;
      this.isDecimalReadOnly = true;

    } else if (this.customCompute.fieldType === 'Percent'
      || this.customCompute.fieldType === 'Unsigned Percent') {

      this.isSizeReadOnly = false;
      this.isDecimalReadOnly = false;

    } else if (this.customCompute.fieldType === 'Period'
      || this.customCompute.fieldType === 'Time') {

      this.customCompute.size = '6';
      this.customCompute.decimal = '';
      this.isSizeReadOnly = true;
      this.isDecimalReadOnly = true;
    }

    this.handleFieldChangedEvent();

    this.changeDetectorRef.markForCheck();
  }

  handleFieldNameEvent(value: string) {
    this.customCompute.fieldName = value;

    if (value) {
      this.fieldNameValidateEventDebouncer$.next('');
    }
    this.handleFieldChangedEvent();
  }

  handleSizeEvent(value: string) {
    this.customCompute.size = value;
    this.handleFieldChangedEvent();
  }

  handleDecimalEvent(value: string) {
    this.customCompute.decimal = value;
    this.handleFieldChangedEvent();
  }

  handleLplTextEvent(value: string) {
    this.customCompute.computeLpl = value;
    const lpl = this.lmComputeBuilderService.convertDerivedComputeCriteria(this.customCompute);
    this.queryValidateEventDebouncer$.next(lpl);
  }

  private handleFieldChangedEvent() {
    this.removeAlertMessages();
    this.preview = this.lmComputeBuilderService.convertDerivedComputeCriteria(this.customCompute);
  }

  private removeAlertMessages() {
    if (this.sizeAlert) {
      this.sizeAlert.removeAllMessages();
    }

    if (this.decimalAlert) {
      this.decimalAlert.removeAllMessages();
    }

    if (this.fieldNameAlert) {
      this.fieldNameAlert.removeAllMessages();
    }

    if (this.fieldTypeAlert) {
      this.fieldTypeAlert.removeAllMessages();
    }
  }

  handleValidationEvent() {

    this.removeAlertMessages();

    if (this.customCompute.fieldType === 'Decimal'
      || this.customCompute.fieldType === 'Unsigned Decimal'
      || this.customCompute.fieldType === 'Percent'
      || this.customCompute.fieldType === 'Unsigned Percent') {

      if (!this.customCompute.decimal) {
        if (this.decimalAlert) {
          this.decimalAlert.addInlineMessage(Soho.Locale.translate('DecimalValueRequired'));
        }
      }

      if (this.customCompute.decimal && this.customCompute.size
        || this.customCompute.decimal && !this.customCompute.size) {

        if (this.customCompute.size) {
          const decimal: number = +this.customCompute.decimal;
          const size: number = +this.customCompute.size;

          if (decimal > size) {
            if (this.decimalAlert) {
              this.decimalAlert.addInlineMessage(Soho.Locale.translate('DecimalValueExceeds'));
            }
          }
        } else {
          if (this.decimalAlert) {
            this.decimalAlert.addInlineMessage(Soho.Locale.translate('DecimalValueExceeds'));
          }
        }
      }
    }

    if (this.customCompute.fieldType === 'Integer') {
      if (this.customCompute.size) {
        const size: number = +this.customCompute.size;

        if (size  !== 1
          && size  !== 2
          && size  !== 4) {
          this.sizeAlert.addInlineMessage(Soho.Locale.translate('ValidIntegerSize'));
        }
      }
    }

    if (!this.customCompute.size) {
      this.sizeAlert.addInlineMessage(Soho.Locale.translate('SizeRequired'));
    }

    if (!this.customCompute.fieldName) {
      this.fieldNameAlert.addInlineMessage(Soho.Locale.translate('FieldNameRequired'));
    }

    if (!this.customCompute.fieldType) {
      this.fieldTypeAlert.addInlineMessage(Soho.Locale.translate('FieldTypeRequired'));
    }
  }

  private validateFieldName(fieldName: string) {
    if (fieldName) {
      let result = this.lmComputeBuilderService.validateFieldName(fieldName)
      if (result !== '' && this.fieldNameAlert) {
        this.fieldNameAlert.addInlineMessage(result);
      }
    }
  }

  hasError(): boolean {
    if (this.sizeAlert.getMessage()
      || this.decimalAlert.getMessage()
      || this.fieldNameAlert.getMessage()
      || this.fieldTypeAlert.getMessage()
      || this.definitionAlert.getMessage()) {
      return true;
    }
  }
}
